import { createContext, ReactNode, useState } from "react";

import { getJson, setJson } from "lib/storage/local-storage";
import { useIsMobileWidth } from "lib/utils/viewport";

type ThemeMode = "light" | "sepia" | "dark";
// todo
export type LayoutDirection = "horizontal" | "vertical";

export type Font =
  | "Arial"
  | "Verdana"
  | "Helvetica"
  | "Times New Roman"
  | "Georgia";

interface SettingsContextType {
  font: string;
  setFont: (value: Font) => void;
  fontSize: number;
  setFontSize: (value: number) => void;
  margins: number;
  setMargins: (value: number) => void;
  lineHeight: number;
  setLineHeight: (value: number) => void;
  cols: number;
  setCols: (value: number) => void;
  themeMode: ThemeMode;
  setThemeMode: (value: ThemeMode) => void;
  textAlignJustify: boolean;
  setTextAlignJustify: (value: boolean) => void;
  wordWrap: boolean;
  setWordWrap: (value: boolean) => void;
  layoutDirection: LayoutDirection;
  setLayoutDirection: (value: LayoutDirection) => void;
  animatePageTurns: boolean;
  setAnimatePageTurns: (value: boolean) => void;
  turnPageOnClick: boolean;
  setTurnPageOnClick: (value: boolean) => void;
}

export const SettingsContext = createContext<SettingsContextType | undefined>(
  undefined
);
SettingsContext.displayName = "SettingsContext";
const STORAGE_KEY = "fb-reader-settings";

interface Settings {
  font: Font;
  fontSize: number;
  cols: number;
  margins: number;
  lineHeight: number;
  textAlignJustify: boolean;
  wordWrap: boolean;
  layoutDirection: LayoutDirection;
  animatePageTurns: boolean;
  turnPageOnClick: boolean;
  themeMode: ThemeMode;
}

const DEFAULT_SETTINGS: Settings = {
  font: "Arial",
  fontSize: 16,
  cols: 2,
  margins: 20,
  lineHeight: 1.5,
  textAlignJustify: true,
  wordWrap: true,
  layoutDirection: "horizontal",
  animatePageTurns: false,
  turnPageOnClick: false,
  themeMode: "sepia",
};

function loadSettings(): Settings {
  const stored = getJson<Partial<Settings>>(STORAGE_KEY);
  return stored ? { ...DEFAULT_SETTINGS, ...stored } : DEFAULT_SETTINGS;
}

type SettingsState = [Settings, (s: Settings) => void];

function useSetting<K extends keyof Settings>(
  key: K,
  state: SettingsState
): [Settings[K], (value: Settings[K]) => void] {
  const [settings, setSettings] = state;
  function setValue(value: Settings[K]) {
    const updated = { ...settings, [key]: value };
    setSettings(updated);
    setJson(STORAGE_KEY, updated);
  }
  return [settings[key], setValue];
}

export const FONT_SIZE_MIN = 12;
export const FONT_SIZE_MAX = 48;
export const MARGINS_MIN = 0;
export const MARGINS_MAX = 100;
export const LINE_HEIGHT_MIN = 1.0;
export const LINE_HEIGHT_MAX = 2.0;
export const LINE_HEIGHT_STEP = 0.05;

export const FONTS = [
  "Arial",
  "Verdana",
  "Helvetica",
  "Times New Roman",
  "Georgia",
] as const;

function applyTheme(theme: ThemeMode) {
  document.documentElement.classList.remove(
    "theme-light",
    "theme-sepia",
    "theme-dark"
  );
  document.documentElement.classList.add(`theme-${theme}`);
}

export function SettingsProvider({ children }: { children: ReactNode }) {
  const isMobile = useIsMobileWidth();
  const settings = useState(() => {
    const loaded = loadSettings();
    applyTheme(loaded.themeMode);
    return loaded;
  });

  const [font, setFont] = useSetting("font", settings);
  const [fontSize, setFontSize] = useSetting("fontSize", settings);
  const [colsSetting, setCols] = useSetting("cols", settings);
  const cols = isMobile ? 1 : colsSetting;
  const [margins, setMargins] = useSetting("margins", settings);
  const [lineHeight, setLineHeight] = useSetting("lineHeight", settings);
  const [textAlignJustify, setTextAlignJustify] = useSetting(
    "textAlignJustify",
    settings
  );
  const [wordWrap, setWordWrap] = useSetting("wordWrap", settings);
  const [layoutDirection, setLayoutDirection] = useSetting(
    "layoutDirection",
    settings
  );
  const [animatePageTurns, setAnimatePageTurns] = useSetting(
    "animatePageTurns",
    settings
  );
  const [turnPageOnClick, setTurnPageOnClick] = useSetting(
    "turnPageOnClick",
    settings
  );
  const [themeMode, _setThemeMode] = useSetting("themeMode", settings);

  function setThemeMode(value: ThemeMode) {
    applyTheme(value);
    _setThemeMode(value);
  }

  return (
    <SettingsContext
      value={{
        font,
        setFont,
        fontSize,
        setFontSize,
        margins,
        setMargins,
        lineHeight,
        setLineHeight,
        cols,
        setCols,
        themeMode,
        setThemeMode,
        textAlignJustify,
        setTextAlignJustify,
        wordWrap,
        setWordWrap,
        layoutDirection,
        setLayoutDirection,
        animatePageTurns,
        setAnimatePageTurns,
        turnPageOnClick,
        setTurnPageOnClick,
      }}
    >
      {children}
    </SettingsContext>
  );
}
